import { useCallback, useEffect, useRef } from "react";

import {
  startPlaySession,
  type PlaySession,
  type PlaySessionRef,
} from "../../../lib/contentPlay";
import type { PlaybackProgress } from "@shared/player/controls";

/**
 * Bind one `StreamSurface` to one content-play session (`ANALYTICS-2`).
 *
 * The mechanism is `lib/contentPlay.ts`; this is the twelve lines of React that
 * keep exactly one session alive per thing being watched, and it exists so the
 * two call sites - a film in `VodScreen` and a channel in `GuideScreen` - cannot
 * drift into two different ideas of when a play begins and ends.
 *
 * ## `ref` is the switch, and null is a real value
 *
 * Pass `null` and any open session is ENDED - its tail reported, nothing new
 * accrued. That is what makes the guide correct: it keeps a pre-warmed spare
 * surface mounted and playing so a channel change does not pay a full player
 * construction, and a spare nobody is watching must not accrue watch time. The
 * caller passes a ref only for the surface that is actually live, and the
 * session follows the live channel rather than the mounted element.
 *
 * ## Why there is no `onPlaying`
 *
 * "Playback began" is not an event the surface can be trusted to give us at the
 * right moment. It fires when the manifest loaded - before a frame - and for a
 * pre-warmed channel it fired minutes before the guest tuned to it. The session
 * decides for itself, from the first sample where the playhead is seen to move;
 * see `lib/contentPlay.ts`. So the only wiring a caller needs is `onProgress`,
 * which every surface already emits, and `onEnded` where an asset can end.
 */
export interface PlayReport {
  /** Wire to `StreamSurface`'s `onProgress`. */
  onProgress: (progress: PlaybackProgress) => void;
  /** Wire to `StreamSurface`'s `onEnded`, where the content can end. */
  onEnded: () => void;
}

const keyOf = (ref: PlaySessionRef | null) =>
  ref ? `${ref.kind} ${ref.assetId}` : null;

export function usePlayReport(ref: PlaySessionRef | null): PlayReport {
  const key = keyOf(ref);
  const refRef = useRef(ref);
  refRef.current = ref;
  const session = useRef<{ key: string; session: PlaySession } | null>(null);

  /**
   * The tail is flushed when the WATCHED THING changes, not when the component
   * does. A guest zapping from one channel to the next unmounts nothing - the
   * surfaces are recycled - so keying the cleanup on the component would report
   * one endless session that started on the first channel of the evening.
   */
  useEffect(
    () => () => {
      session.current?.session.end();
      session.current = null;
    },
    [key]
  );

  const onProgress = useCallback((progress: PlaybackProgress) => {
    const current = refRef.current;
    if (!current) return;
    const k = keyOf(current)!;
    let held = session.current;
    if (!held || held.key !== k) {
      // The effect cleanup above has already ended the previous session on a
      // key change; ending it again is a no-op and is here so a session can
      // never be orphaned in the open set by an ordering this file does not
      // control. Starting one is done in exactly one place, so a re-render
      // cannot fork a second session for the same title.
      held?.session.end();
      held = { key: k, session: startPlaySession(current) };
      session.current = held;
    }
    held.session.sample({
      currentTime: progress.currentTime,
      paused: progress.paused,
      seeking: progress.seeking,
    });
  }, []);

  const onEnded = useCallback(() => {
    session.current?.session.complete();
    session.current = null;
  }, []);

  return { onProgress, onEnded };
}
